import { Button, Popconfirm, Tag, Typography } from "antd";
import type { ButtonProps } from "antd";
import { DeleteOutlined, EditOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { buildNoteCardView } from "@mappers/notes.mapper.mjs";
import { formattedPlayerName } from "@utils/name_utils";
import { formatNoteDate } from "@utils/notesDates.mjs";
import type { Note, NoteAttribute } from "@store/reducers/notes.reducer";
import { FittedTags } from "./FittedTags";
import { ReadersBadge } from "./ReadersBadge";

const DELETE_CONFIRM_PROPS = { danger: true, "data-attr": "notes.note-card.confirm" } as ButtonProps;
const DELETE_CANCEL_PROPS = { "data-attr": "notes.note-card.cancel" } as ButtonProps;

interface NoteCardProps {
    note: Note;
    attributes: NoteAttribute[];
    myUuid: string | undefined;
    timeZone: string;
    teamName: string;
    onDelete: (note: Note) => Promise<void>;
    /** The pencil shows only when this is set and the note has `can_edit`. */
    onEdit?: (note: Note) => void;
    /** False on a Player page, where every note is about the same player. */
    showPlayer: boolean;
}

export const NoteCard = ({ note, attributes, myUuid, timeZone, teamName, onDelete, onEdit, showPlayer }: NoteCardProps) => {
    const view = buildNoteCardView(note, attributes, myUuid);
    const playerName = note.player ? formattedPlayerName(teamName, note.player.name) : null;

    return (
        <article className="notes-card">
            {/* The author leads the card, as in a message. The player the note is about sits at the bottom left, the tags at the bottom right. */}
            <header className="notes-card-head">
                <div className="notes-card-author">
                    <Typography.Text strong>{view.authorLabel}</Typography.Text>
                    <Typography.Text type="secondary">{formatNoteDate(note.created_at, timeZone)}</Typography.Text>
                </div>
                <div className="notes-card-actions">
                    {view.readers.length > 0 && <ReadersBadge readers={view.readers} />}
                    {onEdit && note.can_edit && (
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            aria-label="Edit note"
                            title="Edit note"
                            data-attr="notes.note-card.edit"
                            onClick={() => onEdit(note)}
                        />
                    )}
                    {note.can_delete && (
                        <Popconfirm
                            title="Delete this note?"
                            okText="Delete"
                            okButtonProps={DELETE_CONFIRM_PROPS}
                            cancelButtonProps={DELETE_CANCEL_PROPS}
                            onConfirm={() => onDelete(note)}
                        >
                            <Button
                                type="text"
                                size="small"
                                danger
                                className="notes-card-delete"
                                icon={<DeleteOutlined />}
                                aria-label="Delete note"
                                title="Delete note"
                                data-attr="notes.note-card.delete"
                            />
                        </Popconfirm>
                    )}
                </div>
            </header>
            {/* A long note shows 5 lines. "Show more" opens the rest in place. */}
            <Typography.Paragraph
                className="notes-card-body"
                ellipsis={{ rows: 5, expandable: "collapsible", symbol: (expanded) => (expanded ? "Show less" : "Show more") }}
            >
                {note.body}
            </Typography.Paragraph>
            {/* Attributes are values, so they read as one line of text, not as chips. The title shows them all. */}
            {view.attributes.length > 0 && (
                <div className="notes-card-attrs" title={view.attributes.map((a) => `${a.name}: ${a.value}`).join("\n")}>
                    {view.attributes.map((attribute, i) => (
                        <span key={attribute.id}>
                            {i > 0 && " · "}
                            {attribute.name} <strong>{attribute.value}</strong>
                        </span>
                    ))}
                </div>
            )}
            {(showPlayer || note.tags.length > 0) && (
                <footer className="notes-card-foot">
                    {showPlayer && (
                        <span className="notes-card-player-chip">
                            {playerName ? <UserOutlined /> : <TeamOutlined />}
                            <Typography.Text className="notes-card-player-name">{playerName ?? "General note"}</Typography.Text>
                        </span>
                    )}
                    <FittedTags tags={note.tags} className="notes-card-tags" />
                </footer>
            )}
        </article>
    );
};
